import { defineItemStore } from "../itemStore.store";
import { mapValues } from "@/helpers/utility/CollectionFunctions";
import { useFlowBranchStore } from "./flowBranches.store";
import { calculateExpression } from "@editor/items/expression/expressionFunctions";
import { useSettingsStore } from "@editor/store/settings.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";

import type { FlowItem } from "./FlowItem";
import type { ExpressionValue } from "@editor/items/expression/expressionFunctions";
import type { ExpressionParseContext } from "../effect/Expression";
import { useSceneFlowNodeStore } from "./sceneFlowNodes";

export const flowItemStructure: FlowItem = {
  type: "flow",
  uid: "flow_0",

  title: "",
  description: "",

  valueInputMode: "expression",
  valueExpression: "0",
  valueReferenceType: undefined,
  valueReferenceId: undefined,
  interval: 1,

  id: -1,
  createdAt: 0,

  imageId: null
};

export const useFlowStore = defineItemStore("model/flows", flowItemStructure, {
  getters: {
    withExpressions(state): { [flowId: number]: FlowItem } {
      return mapValues(state.items, (flow) => {
        return {
          ...flow,
          valueExpression: this.expressions[flow.id]
        };
      });
    },
    expressions(state): {
      [flowId: number]: string;
    } {
      return mapValues(state.items, getFlowExpression);
    },

    expressionValues(state): {
      [flowUid: string]: ExpressionValue;
    } {
      const parseContext = getParseContext();

      return mapValues(this.byUid, (flow) => {
        try {
          const flowExpression = this.expressions[flow.id];
          return calculateExpression(flowExpression, parseContext);
        } catch (error) {
          console.error(error);
          return { value: 0, unitValid: false, unit: "", minValue: 0, maxValue: 0, valid: false };
        }
      });
    },

    values(): { [flowId: number]: number } {
      return mapValues(this.items, (flow) => Number(this.expressionValues[flow.uid].value));
    },

    units(): { [flowId: number]: string } {
      return mapValues(this.items, (item) => this.expressionValues[item.uid].unit);
    },

    titles(state): { [flowId: number]: string } {
      return mapValues(state.items, (flow) => {
        if (flow.title) return flow.title;

        const branchStore = useFlowBranchStore();

        const branches = branchStore.byFlow[flow.id] ?? [];

        let title = "";
        const sourceBranches = branches.filter((b) => b.branchType === "source");
        const sourceTitles = sourceBranches.map((branch) => {
          const element = branchStore.elements[branch.id];
          return element?.title ?? "";
        }).join(", ");
        title += (sourceTitles || "...");

        const sinkBranches = branches.filter((b) => b.branchType === "sink");
        const sinkTitles = sinkBranches.map((branch) => {
          const element = branchStore.elements[branch.id];
          return element?.title ?? "";
        }).join(", ");
        title += " ⇒ " + (sinkTitles || "...");

        return title;
      });
    },

    getByLinkedElement() {
      return (elementUid: string): FlowItem[] => this.orderedItems.filter((flow) => {
        const expression = this.expressions[flow.id];
        return expression.includes(elementUid);
      });
    }
  },
  actions: {
    updateBranchesStrength(flow: FlowItem, branchType: "source" | "sink") {
      const flowBranchStore = useFlowBranchStore();
      const allBranches = flowBranchStore.byFlow[flow.id] ?? [];

      const branches = allBranches.filter((b) => b.branchType === branchType);
      const branchesCount = branches.length;
      const multiplier = 1 / branchesCount;

      const flowExpression =
        flow.valueInputMode == "expression"
          ? flow.valueExpression
          : (flow.valueReferenceType ? `${flow.valueReferenceType}_${flow.valueReferenceId}` : '');

      const minMax = calculateExpression(flowExpression, {
        targets: useTargetStore().items,
        resources: useResourceStore().items,
        indices: useIndexStore().indices
      });

      for (const branch of branches) {
        flowBranchStore.updatePointYBounds({
          id: branch.id,
          maxFlowValue: minMax.maxValue,
          minFlowValue: minMax.minValue,
          oldMaxFlowValue: minMax.maxValue,
          oldMinFlowValue: minMax.minValue,
          multiplier: multiplier
        });
      }
    }
  },
  events: {
    async afterDelete(flow: FlowItem) {
      const branches = useFlowBranchStore().byFlow[flow.id] ?? [];
      for (const branch of branches) {
        await useFlowBranchStore().delete(branch);
      }

      const flowNodeStore = useSceneFlowNodeStore();
      const nodes = flowNodeStore.orderedItems.filter((node) => node.flowId === flow.id);
      for (const node of nodes) {
        await flowNodeStore.delete(node);
      }

      return true;
    },
    afterUpdate(flow, updatedData, oldItem) {
      if ("valueInputMode" in updatedData || "valueExpression" in updatedData || "valueReferenceType" in updatedData || "valueReferenceId" in updatedData) {
        const oldExpression = getFlowExpression(oldItem);
        const expression = getFlowExpression(flow);
        const branches = useFlowBranchStore().byFlow[flow.id] ?? [];
        try {
          const oldCalculated = calculateExpression(oldExpression, getParseContext());
          const calculated = calculateExpression(expression, getParseContext());
          for (const branch of branches) {
            useFlowBranchStore().updatePointBounds({
              id: branch.id,
              maxFlowValue: calculated.maxValue,
              minFlowValue: calculated.minValue,
              oldMaxFlowValue: oldCalculated.maxValue,
              oldMinFlowValue: oldCalculated.minValue
            });
          }
        } catch (error) {
          console.error(error);
        }
      }
    },
  }
});

function getFlowExpression(flow: FlowItem) {
  if (!flow) return "";
  if (flow.valueInputMode === "reference") {
    if (!flow.valueReferenceType || flow.valueReferenceId == null) return "";
    return `${flow.valueReferenceType}_${flow.valueReferenceId}`;
  }
  return flow.valueExpression;
}

function getParseContext(): ExpressionParseContext {
  const settings = useSettingsStore();
  const targetStore = useTargetStore();
  const resourceStore = useResourceStore();
  const indexStore = useIndexStore();

  const elementValues = {
    ...targetStore.expressionValues,
    ...resourceStore.expressionValues,
    ...indexStore.expressionValues
  };

  return {
    targets: targetStore.items,
    resources: resourceStore.items,
    indices: indexStore.items,
    roundIndex: 0,
    simulationSettings: {
      roundUnit: settings.simulationRoundUnit,
      roundLength: settings.simulationRoundLength,
      roundAmount: settings.simulationRoundAmount,
      timeStart: settings.simulationTimeStart
    },
    rounds: [{
      index: 0,
      elementValues: elementValues,
      startElementValues: elementValues
    }]
  };
}